import { lazy, Suspense } from 'react';
import { useAcceptingOrders } from '../../hooks/useAcceptingOrders';
import { useOptionalAuth } from '../../contexts/AuthContext';
import PublicLayout from './PublicLayout';
import PreviewBanner from './PreviewBanner';

const HoldingPage = lazy(() => import('../../pages/HoldingPage'));

/** Roles that may preview the storefront while the store is Closed (#38). */
const STAFF_ROLES = new Set(['admin', 'owner', 'driver']);

/**
 * Gate in front of every customer-facing route. While the store is Closed
 * (`acceptingOrders` off) the entire customer surface is replaced by the
 * holding page — catalog, gallery, and the order wizard are unreachable —
 * EXCEPT for authenticated staff (admin/owner/driver), who see the real
 * storefront with a preview banner so the team can review the site and place
 * prod test orders before launch. When the store is open, the normal storefront
 * (PublicLayout + routed page) renders for everyone.
 *
 * Only customer routes pass through here; `/admin`, `/driver`, and auth routes
 * are separate route groups and remain reachable regardless of the flag.
 *
 * The staff bypass keys off the verified Cognito ROLE (via the shared root
 * AuthProvider), NOT mere authentication, so a future lightweight customer
 * account cannot slip past the closed gate. The same role check is enforced
 * server-side in the order-creation guard (#38).
 */
function GateSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-page">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
    </div>
  );
}

export default function CustomerRouteGate() {
  const status = useAcceptingOrders();
  const auth = useOptionalAuth();
  const isStaff = auth ? STAFF_ROLES.has(auth.userRole) : false;

  if (status === 'loading') {
    return <GateSpinner />;
  }

  if (status === 'closed') {
    // Staff preview: show the real storefront with a banner instead of the
    // holding page. Everyone else (anonymous, customers, viewers) sees Closed.
    if (isStaff) {
      return (
        <>
          <PreviewBanner />
          <PublicLayout />
        </>
      );
    }
    return (
      <Suspense fallback={<GateSpinner />}>
        <HoldingPage />
      </Suspense>
    );
  }

  return <PublicLayout />;
}
